import React, { useRef, useState } from "react";
import { toast } from "react-toastify";
import uploadFiles from "./studioUpload";
import { ACCENT } from "./shared";

const MAX_BYTES = 10 * 1024 * 1024; // matches the API's 10MB S3 limit

function validate(file, allowedTypes) {
  if (!allowedTypes.includes(file.type)) return `${file.name}: unsupported file type.`;
  if (file.size > MAX_BYTES) return `${file.name}: file is larger than 10MB.`;
  return null;
}

// Round profile-picture picker; uploads on selection and reports the URL.
export function ProfileImageUploader({ value, onChange, disabled }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const onPick = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    const problem = validate(file, ["image/jpeg", "image/png"]);
    if (problem) {
      toast.error(problem);
      return;
    }
    setUploading(true);
    try {
      const [uploaded] = await uploadFiles([file]);
      onChange(uploaded.url);
    } catch (e) {
      toast.error(e.message || "Upload failed. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="flex items-center gap-4">
      <div className="w-20 h-20 rounded-full bg-gray-100 border border-gray-200 overflow-hidden flex items-center justify-center text-gray-400 text-xs">
        {value ? <img src={value} alt="Artist profile" className="w-full h-full object-cover" /> : "No image"}
      </div>
      <div>
        <input ref={inputRef} type="file" accept="image/jpeg,image/png" className="hidden" onChange={onPick} />
        <button
          type="button"
          disabled={disabled || uploading}
          onClick={() => inputRef.current?.click()}
          className="rounded-full border border-gray-200 bg-white px-4 py-1.5 text-sm font-semibold hover:bg-gray-50 disabled:opacity-50"
        >
          {uploading ? "Uploading..." : value ? "Change image" : "Upload image"}
        </button>
        {value && !uploading && (
          <button type="button" onClick={() => onChange("")} className="ml-3 text-sm text-gray-500 underline">
            Remove
          </button>
        )}
        <p className="text-gray-500 text-xs mt-1">JPG or PNG, up to 10MB.</p>
      </div>
    </div>
  );
}

// Upload box for identity documents (front and back, JPG/PNG/PDF).
export function IdDocumentUploader({ urls, onChange, error }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const onPick = async (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = "";
    if (!files.length) return;
    if (urls.length + files.length > 2) {
      toast.error("You can upload at most 2 files (front and back).");
      return;
    }
    for (const f of files) {
      const problem = validate(f, ["image/jpeg", "image/png", "application/pdf"]);
      if (problem) {
        toast.error(problem);
        return;
      }
    }
    setUploading(true);
    try {
      const uploaded = await uploadFiles(files);
      onChange([...urls, ...uploaded.map((u) => u.url)]);
    } catch (e) {
      toast.error(e.message || "Upload failed. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      <div
        className={`flex items-center justify-between rounded-xl border px-4 py-3 bg-white ${
          error ? "border-red-400" : "border-gray-200"
        }`}
      >
        <span className="text-sm text-gray-500">
          {urls.length ? `${urls.length} file${urls.length > 1 ? "s" : ""} uploaded` : "Upload front and back (JPG, PNG or PDF)"}
        </span>
        <div className="flex items-center gap-3">
          {urls.length > 0 && (
            <button type="button" onClick={() => onChange([])} className="text-sm text-gray-500 underline">
              Clear
            </button>
          )}
          <input
            ref={inputRef}
            type="file"
            multiple
            accept="image/jpeg,image/png,application/pdf"
            className="hidden"
            onChange={onPick}
          />
          <button
            type="button"
            disabled={uploading || urls.length >= 2}
            onClick={() => inputRef.current?.click()}
            className="text-sm font-bold disabled:opacity-50"
            style={{ color: ACCENT }}
          >
            {uploading ? "Uploading..." : "Browse"}
          </button>
        </div>
      </div>
    </div>
  );
}

// Small button that uploads one JPG/PNG and reports its URL (cover image, profile photo).
// With children, the button shows them instead of the label (for example a clickable avatar); `label` then names the button.
export function ImageUploadButton({ label, onUploaded, className = "", children }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const onPick = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    const problem = validate(file, ["image/jpeg", "image/png"]);
    if (problem) {
      toast.error(problem);
      return;
    }
    setUploading(true);
    try {
      const [uploaded] = await uploadFiles([file]);
      onUploaded(uploaded.url);
    } catch (e) {
      toast.error(e.message || "Upload failed. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <>
      <input ref={inputRef} type="file" accept="image/jpeg,image/png" className="hidden" onChange={onPick} />
      <button type="button" disabled={uploading} onClick={() => inputRef.current?.click()} className={className} aria-label={children ? label : undefined} title={children ? label : undefined}>
        {children ? (uploading ? <span className="flex h-full w-full items-center justify-center text-[10px] font-semibold text-white">...</span> : children) : uploading ? "Uploading..." : label}
      </button>
    </>
  );
}
